/**
 * API Client pour la gestion des catégories de produits
 */
import { safeFetch, safeJsonParse, getAuthHeaders } from './api';
import type { ProductCategory } from '../types/models';

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001/api/v1";


/**
 * API Catégories
 */
export const categoriesAPI = {
  /**
   * Récupérer toutes les catégories (format plat)
   */
  async getAll(): Promise<ProductCategory[]> {
    const response = await safeFetch(`${API_URL}/catalog/categories`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Erreur lors de la récupération des catégories : ${response.statusText}`);
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || [];
  },

  /**
   * Récupérer une catégorie par ID
   */
  async getById(categoryId: string): Promise<ProductCategory> {
    const response = await safeFetch(`${API_URL}/catalog/categories/${categoryId}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Erreur lors de la récupération de la catégorie : ${response.statusText}`);
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data;
  },

  /**
   * Récupérer catégories racines avec enfants (format hiérarchique)
   */
  async getRootCategories(): Promise<ProductCategory[]> {
    const response = await safeFetch(`${API_URL}/catalog/categories/roots`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Erreur lors de la récupération des catégories racines : ${response.statusText}`);
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || [];
  },

  /**
   * Récupérer les sous-catégories d'une catégorie
   */
  async getChildren(categoryId: string): Promise<ProductCategory[]> {
    const response = await safeFetch(`${API_URL}/catalog/categories/${categoryId}/children`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Erreur lors de la récupération des sous-catégories : ${response.statusText}`);
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || [];
  },

  /**
   * Créer une catégorie
   */
  async create(data: {
    name: string;
    description?: string;
    parentId?: string;
    color?: string;
    icon?: string;
    order?: number;
  }): Promise<ProductCategory> {
    const response = await safeFetch(`${API_URL}/catalog/categories`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`Erreur lors de la création de la catégorie : ${response.statusText}`);
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data;
  },

  /**
   * Mettre à jour une catégorie
   */
  async update(id: string, data: Partial<ProductCategory>): Promise<ProductCategory> {
    const response = await safeFetch(`${API_URL}/catalog/categories/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`Erreur lors de la mise à jour de la catégorie : ${response.statusText}`);
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data;
  },

  /**
   * Supprimer une catégorie
   */
  async delete(id: string): Promise<{ success: boolean }> {
    const response = await safeFetch(`${API_URL}/catalog/categories/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Erreur lors de la suppression de la catégorie : ${response.statusText}`);
    }

    return safeJsonParse(response);
  },

  /**
   * Déplacer une catégorie (changer de parent)
   */
  async move(id: string, newParentId: string | null): Promise<ProductCategory> {
    const response = await safeFetch(`${API_URL}/catalog/categories/${id}/move`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ parentId: newParentId }),
    });

    if (!response.ok) {
      throw new Error(`Erreur lors du déplacement de la catégorie : ${response.statusText}`);
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data;
  },

  /**
   * Récupérer les statistiques des catégories
   */
  async getStats(): Promise<{
    totalCategories: number;
    categoriesWithProducts: number;
    emptyCategories: number;
    byCategory: Array<{ id: string; name: string; productCount: number }>;
  }> {
    const response = await safeFetch(`${API_URL}/catalog/categories/stats`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Erreur lors de la récupération des statistiques : ${response.statusText}`);
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data;
  },
};
